import { ChangeDetectionStrategy, Component, OnInit, Input, signal, computed, inject, input, output } from "@angular/core";
import { UserService } from "../../../../../core-ui/services/user.service";
import { IContact } from "../../../../../core-ui/models/Appointment";
import { NotificationService } from "../../../../../core-ui/services/notification.service";
import { PageableRequestBuilder } from "../../../../../core-ui/models/Pageable";
import { IFilterSelection } from "../../../../../core-ui/models/interfaces";
import { Util } from "@auvious/common";

@Component({
  selector: "av-agent-filter",
  templateUrl: "./agent-filter.component.html",
  styleUrls: ["./agent-filter.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AgentFilterComponent implements OnInit {
  private user = inject(UserService);
  private notification = inject(NotificationService);

  @Input()
  set selection(value: string[]) {
    if (value) {
      this.selectedContacts.set(value);
    }
  }

  readonly type = input<"radio" | "checkbox">("checkbox");

  readonly selectionChange = output<string[]>();
  readonly ready = output<IContact[]>();

  contacts = signal<IContact[]>([]);
  contactsFiltered = signal<IContact[]>([]);
  loading = signal(false);
  contactNames = signal<{ [id: string]: string }>({});
  selectedContacts = signal<string[]>([]);

  isEmptySearch = signal(false);

  // unique per instance, so that separate agent filters never share a radio group
  readonly groupName = `agent-filter-${Util.uuidgen()}`;

  ngOnInit(): void {
    this.getAgents();
  }

  async getAgents() {
    let currentPage = 1;
    let isLastPage = false;
    while (!isLastPage) {
      try {
        this.loading.set(true);
        if (currentPage === 1) {
          this.contacts.set([]);
        }
        const query = new PageableRequestBuilder()
          .pageNumber(currentPage)
          .pageSize(50)
          .build();
        const pager = await this.user.queryContacts(query);

        isLastPage = pager?.pageCount === pager?.pageNumber;

        this.contacts.update((c) => {
          const map = new Map(c.map((contact) => [contact.id, contact]));
          pager.entities.forEach((contact) => map.set(contact.id, contact));
          return Array.from(map.values());
        });
        this.contactsFiltered.set(this.contacts());
        this.contactNames.set(
          this.contacts().reduce(
            (map, c) => ({ ...map, [c.id]: this.getName(c) }),
            {},
          ),
        );
        currentPage++;
      } catch (ex) {
        break;
      } finally {
        if (isLastPage) {
          this.loading.set(false);
          this.ready.emit(this.contacts());
        }
      }
    }
  }

  getName(member: IContact) {
    return member.name?.firstName || member.name?.lastName
      ? [member.name?.firstName, member.name?.lastName].join(" ").trim()
      : member.contact.primaryEmail;
  }

  async queryChange(query: string) {
    const qr = query.toLowerCase();
    const filtered = this.contacts().filter((c) =>
      this.getName(c).toLowerCase().includes(qr),
    );

    this.contactsFiltered.set(filtered);

    this.isEmptySearch.set(
      query?.length > 0 && this.contactsFiltered().length === 0,
    );
  }

  selectionChanged(selection: IFilterSelection<IContact>) {
    switch (this.type()) {
      case "checkbox":
        this.selectedContacts.update((sc) =>
          selection.checked
            ? [...sc, selection.value.id]
            : sc.filter((c) => c !== selection.value.id),
        );
        break;
      case "radio":
        this.selectedContacts.set(
          selection.checked ? [selection.value.id] : [],
        );
        break;
    }
    this.selectionChange.emit(this.selectedContacts());
  }

  isChecked(id: string) {
    return computed(() => this.selectedContacts().includes(id));
  }
}
